import { createSlice } from "@reduxjs/toolkit";
import { RootState } from "../app/store";
import { ICalendarizedSubjects, IOfferedSubject, IOfferedSubjectSection, IQuota } from "../types";
import {
  updateCupoCursoBuilder,
  getCuposAlumnoListBuilder,
  getCatalogoCondicionAprobCursoBuilder,
  toggleMateriaFijadaBuilder,
  getCursoBuilder,
} from "../reduxAsyncThunks/subjects.thunks";
import { changeSectionBuilder, reserveSectionBuilder } from "../reduxAsyncThunks/transaction.thunks";
import { IInscribedSubject } from "../types/inscribedSubjects";
import { CoreSeccionOferta } from "../types/oferta";

interface ISelectedOfferedSubjects {
  idS: number;
  subjectId: number;
}

export interface ISubjectQuotas {
  [key: number]: IQuota; // clave idCupo
}

/** Forma mínima de una sección de la oferta para sembrar sus cupos en carga. */
interface ISeccionConCupo {
  idCurso: number;
  anioLectivo: number;
  subjectData?: { reserveData?: { idC?: number } };
}

export interface ICuposValueSocket {
  anioLectivo: number;
  cantidad: number;
  error: boolean;
  errorMessage: string;
  idCupo: number;
  idCurso: number;
  semaforo: number;
  lastUpdateTimestamp: number;
};
export interface IPanelSubjectData {
  catalogo: string | null;
  condicionAprob: string | null;
  link: string | null;
  idCurso: number;
  anioLectivo: number;
  idAlumnoPrograma: number;
}

export interface ISubjectsSliceState {
  inscribedSubjects: IInscribedSubject[] | null;
  inscribedSubjectsWithoutFilters: IInscribedSubject[] | null;
  offeredSubjects: IOfferedSubject[] | null;
  selectedOfferedSubjects: ISelectedOfferedSubjects[];
  loadingRequestInTransit: boolean;
  subjectsQuotas: ISubjectQuotas;
  pinnedSubjectsList: { idCurso: number; anioLectivo: number }[];
  calendarSubjectsList: ICalendarizedSubjects[];
  detailSubjectOffered: IOfferedSubjectSection | null;
  detailSubjectInscribed: IInscribedSubject | null;
  filterTagsList: string[] | null;
  selectedFilterTagsList: string[] | null;
  searchSubjectsValue: string;
  unsubProcessSectionIds: { idCurso: number; anioLectivo: number }[]; // IDs de cursos que están en proceso de desinscripción
  subcriptionProcessSectionIds: { idCurso: number; anioLectivo: number }[]; // IDs de cursos que están en proceso de reserva
  sectionChangeInProcess: { idCurso: number; anioLectivo: number }[]; // IDs de cursos que están en proceso de cambio de sección
  failedSubcriptionProcessIds: { idCurso: number; anioLectivo: number }[]; // IDs de cursos que fallaron en reserva de forma asincronica - WEBSOCKET
  failedSubcriptionSyncProcessIds: { idCurso: number; anioLectivo: number }[]; // IDs de cursos que fallaron en reserva de forma sincronica - Response API
  panelSubjectsDataList: IPanelSubjectData[];
  getCuposAlumnoFetched: boolean;
  // Consulta de cupos en vuelo: la inicialización y el websocket compiten por pedirlos.
  getCuposAlumnoInFlight: boolean;
  cursosToUpdateQuotaList: { idCurso: number; anioLectivo: number }[];
}

const initialState: ISubjectsSliceState = {
  inscribedSubjects: null,
  inscribedSubjectsWithoutFilters: null,
  offeredSubjects: null,
  selectedOfferedSubjects: [],
  loadingRequestInTransit: false,
  subjectsQuotas: {},
  pinnedSubjectsList: [],
  calendarSubjectsList: [],
  detailSubjectOffered: null,
  detailSubjectInscribed: null,
  filterTagsList: null,
  selectedFilterTagsList: null,
  searchSubjectsValue: "",
  unsubProcessSectionIds: [],
  subcriptionProcessSectionIds: [],
  failedSubcriptionProcessIds: [],
  failedSubcriptionSyncProcessIds: [],
  sectionChangeInProcess: [],
  panelSubjectsDataList: [],
  getCuposAlumnoFetched: false,
  getCuposAlumnoInFlight: false,
  cursosToUpdateQuotaList: [],
};

export const subjectsSlice = createSlice({
  name: "subjects",
  initialState,
  reducers: {
    setInscribedSubjects: (state, action) => {
      state.inscribedSubjects = action.payload;
    },
    setInscribedSubjectsWithoutFilters: (state, action) => {
      state.inscribedSubjectsWithoutFilters = action.payload;
    },
    setOfferedSubjects: (state, action) => {
      state.offeredSubjects = action.payload;
      // Los cupos se piden recién después de la oferta: hasta que respondan
      // getCuposAlumnoFijados/getCuposAlumno la sección muestra el loader.
      const secciones: ISeccionConCupo[] = (action.payload ?? []).flatMap(
        (subject: { sections?: ISeccionConCupo[] }) => subject.sections ?? []
      );
      secciones.forEach((section) => {
        const idCupo = section.subjectData?.reserveData?.idC ?? -1;
        if (idCupo <= 0 || state.subjectsQuotas[idCupo]) return;
        state.subjectsQuotas[idCupo] = {
          quotaId: idCupo,
          qty: 0,
          availability: 0,
          cursoId: section.idCurso,
          anioLectivo: section.anioLectivo,
          readyToUse: false,
          lastUpdateTimestamp: 0,
        };
      });
    },
    removeVisibleOfferedSubject: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const { idCurso, anioLectivo } = action.payload;
      const subject = state.offeredSubjects?.find((i) =>
        i.sections.some((s) => s.anioLectivo === anioLectivo && s.idCurso === idCurso)
      );
      if (subject) {
        const newSections = subject.sections?.map((i) =>
          i.idCurso === idCurso && i.anioLectivo === anioLectivo ? { ...i, visibleEnOferta: false } : i
        );
        state.offeredSubjects =
          state.offeredSubjects?.map((i) => (i.id === subject.id ? { ...subject, sections: newSections || [] } : i)) ||
          [];
      }
    },
    addInscribedSubject: (state, action) => {
      if (state.inscribedSubjects) {
        state.inscribedSubjects = [...state.inscribedSubjects, action.payload];
      }
    },
    removeInscribedSubject: (state, action) => {
      if (state.inscribedSubjects) {
        const newInscribedSubjects = state.inscribedSubjects.filter((i) => i.sections[0].idCurso !== action.payload);
        state.inscribedSubjects = newInscribedSubjects;
      }
    },
    addSelectedOfferedSubject: (state, action) => {
      state.selectedOfferedSubjects = [...state.selectedOfferedSubjects, action.payload];
    },
    removeSelectedOfferedSubject: (state, action) => {
      const newSelectedOfferedSubjects = state.selectedOfferedSubjects.filter((i) => i.idS !== action.payload);
      state.selectedOfferedSubjects = newSelectedOfferedSubjects;
    },
    addSectionChangeInProcess: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const index = state.sectionChangeInProcess.findIndex(
        (item) => item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo
      );
      if (index === -1) {
        state.sectionChangeInProcess.push(action.payload);
      }
    },
    removeSectionChangeInProcess: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const newSectionChangeInProcess = state.sectionChangeInProcess.filter(
        (item) => item.idCurso !== action.payload.idCurso && item.anioLectivo !== action.payload.anioLectivo
      );
      state.sectionChangeInProcess = newSectionChangeInProcess;
    },
    clearSelectedOfferedSubject: (state) => {
      state.selectedOfferedSubjects = [];
    },
    setLoadingRequestInTransit: (state, { payload }) => {
      state.loadingRequestInTransit = payload;
    },
    setSubjectsQuota: (state, action: { payload: ISubjectQuotas }) => {
      state.subjectsQuotas = action.payload;
    },
    mergeSubjectsQuotas: (state, action: { payload: ISubjectQuotas }) => {
      state.subjectsQuotas = { ...state.subjectsQuotas, ...action.payload };
    },
    setPinnedSubjectsList: (state, action) => {
      state.pinnedSubjectsList = action.payload;
    },
    addCalendarSubject: (state, action: { payload: { idCurso: number; anioLectivo: number; idMateria: number } }) => {
      if (state.calendarSubjectsList) {
        state.calendarSubjectsList = [...state.calendarSubjectsList, action.payload];
      }
    },
    removeCalendarSubject: (
      state,
      action: { payload: { idCurso: number; anioLectivo: number; idMateria: number } }
    ) => {
      if (state.calendarSubjectsList) {
        const newCalendarSubjects = state.calendarSubjectsList.filter(
          (i) =>
            !(
              i.idCurso === action.payload.idCurso &&
              i.anioLectivo === action.payload.anioLectivo &&
              i.idMateria === action.payload.idMateria
            )
        );
        state.calendarSubjectsList = newCalendarSubjects;
      }
    },
    removeAllCalendarSubject: (state) => {
      state.calendarSubjectsList = [];
    },
    setDetailSubjectOffered: (state, action: { payload: { subjectId: number; cursoId: number } | null }) => {

      if (action.payload === null) {
        state.detailSubjectOffered = null;
        return;
      }
      const { subjectId, cursoId } = action.payload;
      const materia = state.offeredSubjects?.filter((sub) => sub.id === subjectId);

      if (materia && materia[0]) {
        const curso = materia[0].sections.filter((curso: IOfferedSubjectSection) => curso.idCurso === cursoId);
        if (curso[0]) {
          state.detailSubjectOffered = curso[0];
        } else {
          state.detailSubjectInscribed = null;
        }
      } else {
        state.detailSubjectInscribed = null;
      }
    },
    setFilterTagsList: (state, action) => {
      state.filterTagsList = action.payload;
    },
    setSelectedFilterTagsList: (state, action) => {
      state.selectedFilterTagsList = action.payload;
    },
    setSearchSubjectsValue: (state, { payload }) => {
      state.searchSubjectsValue = payload;
    },
    setUnsubProcessSectionIds: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const index = state.unsubProcessSectionIds.findIndex(
        (item) => item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo
      );
      if (index === -1) {
        state.unsubProcessSectionIds.push(action.payload);
      }
    },
    deleteUnsubProcessSectionId: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const index = state.unsubProcessSectionIds.findIndex(
        (item) => item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo
      );
      if (index !== -1) {
        state.unsubProcessSectionIds.splice(index, 1);
      }
    },
    clearUnsubProcessSectionIds: (state) => {
      state.unsubProcessSectionIds = [];
    },
    addSubProcessSectionIds: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const index = state.subcriptionProcessSectionIds.findIndex(
        (item) => item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo
      );
      if (index === -1) {
        state.subcriptionProcessSectionIds.push(action.payload);
      }
    },
    removeSubProcessSectionIds: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const index = state.subcriptionProcessSectionIds.findIndex(
        (item) => item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo
      );
      if (index !== -1) {
        state.subcriptionProcessSectionIds.splice(index, 1);
      }
    },
    removeFromInscribedSubjects: (state, action) => {
      const cursoId = action.payload;
      if (state.inscribedSubjects) {
        state.inscribedSubjects = state.inscribedSubjects.filter((subject) => subject.sections[0].idCurso !== cursoId);
      }
    },
    setFailedProcessingSubject: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      state.failedSubcriptionProcessIds.push(action.payload);
    },
    removeFailedProcessingSubject: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const index = state.failedSubcriptionProcessIds.findIndex(
        (item) => item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo
      );
      if (index !== -1) {
        state.failedSubcriptionProcessIds.splice(index, 1);
      }
    },
    removeFailedSyncProcessingSubject: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const index = state.failedSubcriptionSyncProcessIds.findIndex(
        (item) => item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo
      );
      if (index !== -1) {
        state.failedSubcriptionSyncProcessIds.splice(index, 1);
      }
      const indexAsyncIds = state.failedSubcriptionProcessIds.findIndex(
        (item) => item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo
      );
      if (indexAsyncIds !== -1) {
        state.failedSubcriptionProcessIds.splice(index, 1);
      }
    },
    updateCuposValue: (state, action) => {
      console.log(`[cupos_value_ws] ${action.payload.quotas.length} cupos recibidos vía socket`);
      action.payload.quotas.forEach((newQuota: ICuposValueSocket) => {
        if (!newQuota.error && state.subjectsQuotas[newQuota.idCupo]) {
          state.subjectsQuotas[newQuota.idCupo] = {
            quotaId: newQuota.idCupo,
            cursoId: newQuota.idCurso,
            anioLectivo: newQuota.anioLectivo,
            availability: newQuota.semaforo,
            qty: newQuota.cantidad,
            readyToUse: true,
            lastUpdateTimestamp: newQuota.lastUpdateTimestamp,
          };
        }
      });
    },
    updateSingleCupoValue: (state, action: { payload: ICuposValueSocket }) => {
      const cupo = action.payload;
      if (!cupo.error && state.subjectsQuotas[cupo.idCupo]) {
        state.subjectsQuotas[cupo.idCupo] = {
          quotaId: cupo.idCupo,
          cursoId: cupo.idCurso,
          anioLectivo: cupo.anioLectivo,
          availability: cupo.semaforo,
          qty: cupo.cantidad,
          readyToUse: true,
          lastUpdateTimestamp: cupo.lastUpdateTimestamp,
        };
      }
    },
    setAllQuotasReadyToUse: (state) => {
      const updatedQuotas = { ...state.subjectsQuotas };
      for (const key in updatedQuotas) {
        if (!updatedQuotas[key].readyToUse) {
          updatedQuotas[key] = { ...updatedQuotas[key], readyToUse: true };
        }
      }
      state.subjectsQuotas = updatedQuotas;
    },
    removePanelSubjectsData: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const { idCurso, anioLectivo } = action.payload;
      state.panelSubjectsDataList = state.panelSubjectsDataList.filter(
        (item) => !(item.idCurso === idCurso && item.anioLectivo === anioLectivo),
      );
    },
    clearPanelSubjectsDataList: (state) => {
      state.panelSubjectsDataList = [];
    },
    updateSeccionOfertaValue: (state, action: { payload: CoreSeccionOferta }) => {
      const { id_materia, id_curso, data } = action.payload;
      const newSection = data as unknown as IOfferedSubjectSection;
      // CASO: No existe la oferta
      if (!state.offeredSubjects) {
        state.offeredSubjects = [{ id: id_materia, title: newSection.title ?? "", tipoMateria: newSection.tipoMateria ?? "", sections: [newSection] }];
        return;
      }
      // CASO: No existe la materia en la oferta
      const subject = state.offeredSubjects.find((s) => s.id === id_materia);

      if (!subject) {
        state.offeredSubjects.push({ id: id_materia, title: newSection.title ?? "", tipoMateria: newSection.tipoMateria ?? "", sections: [newSection] });
        return;
      }

      const section = subject.sections.find((s) => s.idCurso === id_curso);

      if (section) {
        // CASO: Existe la sección en la materia - se actualiza la sección
        Object.assign(section, newSection);
      } else {
        // CASO: No existe la sección en la materia - se inserta nueva sección ordenada alfabéticamente
        const newSectionName = newSection.section ?? "";
        const insertIndex = subject.sections.findIndex((s) => (s.section ?? "").localeCompare(newSectionName, undefined, { numeric: true, sensitivity: "base" }) > 0);
        if (insertIndex === -1) {
          subject.sections.push(newSection);
        } else {
          subject.sections.splice(insertIndex, 0, newSection);
        }
      }
    },
    removeSeccionOferta: (state, action: { payload: { id_materia: number; id_curso: number, anio_lectivo: number } }) => {
      const { id_materia, id_curso, anio_lectivo } = action.payload;
      const subject = state.offeredSubjects?.find((s) => s.id === id_materia);
      if (subject) {
        subject.sections = subject.sections.filter((s) => s.idCurso !== id_curso || s.anioLectivo !== anio_lectivo);
      }
    },
    addCursoToUpdateQuota: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      const exists = state.cursosToUpdateQuotaList.some(
        (item) => item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo
      );
      if (!exists) {
        state.cursosToUpdateQuotaList.push(action.payload);
      }
    },
    removeCursoFromUpdateQuota: (state, action: { payload: { idCurso: number; anioLectivo: number } }) => {
      state.cursosToUpdateQuotaList = state.cursosToUpdateQuotaList.filter(
        (item) => !(item.idCurso === action.payload.idCurso && item.anioLectivo === action.payload.anioLectivo)
      );
    },
    setGetCuposAlumnoFetched: (state, action: { payload: boolean }) => {
      state.getCuposAlumnoFetched = action.payload;
    },
  },
  extraReducers(builder) {
    updateCupoCursoBuilder(builder);
    reserveSectionBuilder(builder);
    getCuposAlumnoListBuilder(builder);
    getCatalogoCondicionAprobCursoBuilder(builder);
    toggleMateriaFijadaBuilder(builder);
    changeSectionBuilder(builder);
    getCursoBuilder(builder);
  },
});

export const selectInscribedSubjects = (state: RootState) => state.subjects.inscribedSubjects;

export const selectInscribedSubjectsWithoutFilters = (state: RootState) =>
  state.subjects.inscribedSubjectsWithoutFilters;

export const selectOfferedSubjects = (state: RootState) => state.subjects.offeredSubjects;

/* export const selectSelectedOfferedSubjects = (state: RootState) => state.subjects.selectedOfferedSubjects; */

/* export const selectLoadingRequestInTransit = (state: RootState) => state.subjects.loadingRequestInTransit; */

export const selectSubjectsQuotas = (state: RootState) => state.subjects.subjectsQuotas;

export const selectPinnedSubjectsList = (state: RootState) => state.subjects.pinnedSubjectsList;

export const selectCalendarSubjectsList = (state: RootState) => state.subjects.calendarSubjectsList;

export const selectDetailSubjectOffered = (state: RootState) => state.subjects.detailSubjectOffered;

export const selectFilterTagsList = (state: RootState) => state.subjects.filterTagsList;

export const selectSelectedFilterTagsList = (state: RootState) => state.subjects.selectedFilterTagsList;

export const selectSearchSubjectsValue = (state: RootState) => state.subjects.searchSubjectsValue;

export const selectUnsubProcessSectionIds = (state: RootState) => state.subjects.unsubProcessSectionIds;

export const selectSubcriptionProcessSectionIds = (state: RootState) => state.subjects.subcriptionProcessSectionIds;

export const selectFailedSubcriptionIds = (state: RootState) => state.subjects.failedSubcriptionProcessIds;

export const selectFailedSubcriptionSyncIds = (state: RootState) => state.subjects.failedSubcriptionSyncProcessIds;

export const selectSectionChangeInProcess = (state: RootState) => state.subjects.sectionChangeInProcess;

export const selectPanelSubjectsDataList = (state: RootState) => state.subjects.panelSubjectsDataList;

export const selectGetCuposAlumnoFetched = (state: RootState) => state.subjects.getCuposAlumnoFetched;

export const selectCursosToUpdateQuotaList = (state: RootState) => state.subjects.cursosToUpdateQuotaList;

export const {
  setInscribedSubjects,
  setInscribedSubjectsWithoutFilters,
  setOfferedSubjects,
  addInscribedSubject,
  removeInscribedSubject,
  addSelectedOfferedSubject,
  removeSelectedOfferedSubject,
  clearSelectedOfferedSubject,
  setLoadingRequestInTransit,
  setSubjectsQuota,
  mergeSubjectsQuotas,
  setPinnedSubjectsList,
  addCalendarSubject,
  removeCalendarSubject,
  setDetailSubjectOffered,
  removeAllCalendarSubject,
  setFilterTagsList,
  setSelectedFilterTagsList,
  setSearchSubjectsValue,
  setUnsubProcessSectionIds,
  clearUnsubProcessSectionIds,
  addSubProcessSectionIds,
  removeSubProcessSectionIds,
  deleteUnsubProcessSectionId,
  setFailedProcessingSubject,
  removeFailedProcessingSubject,
  removeFailedSyncProcessingSubject,
  addSectionChangeInProcess,
  removeSectionChangeInProcess,
  updateCuposValue,
  updateSingleCupoValue,
  removeFromInscribedSubjects,
  removePanelSubjectsData,
  clearPanelSubjectsDataList,
  removeVisibleOfferedSubject,
  setAllQuotasReadyToUse,
  updateSeccionOfertaValue,
  removeSeccionOferta,
  setGetCuposAlumnoFetched,
  addCursoToUpdateQuota,
  removeCursoFromUpdateQuota,
} = subjectsSlice.actions;

export default subjectsSlice.reducer;
